import { useEffect, useState } from "react";
import { Phone, CheckCircle2, Clock, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { whatsappHref } from "@/features/customers/lib/phoneUtils";

function telHref(tel) {
  const t = (tel || "").trim();
  if (!t) return null;
  if (t.startsWith("tel:")) return t;
  return `tel:${t}`;
}

function formatCountdown(seconds) {
  const s = Math.max(0, Number(seconds) || 0);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

/**
 * Decorator mandatory customer calls: before decorating and after sends (1 min each).
 */
export default function ProductionCustomerCall({
  info,
  callInfo,
  phase = "before",
  busy,
  done,
  onStartCall,
  onStartWhatsAppCall,
  onCompleteCall,
  starting,
  completing,
  variant = "default",
}) {
  const call = callInfo || info?.customer_call || {};
  const confirmed = Boolean(call.is_confirmed);
  const callHref = telHref(info?.customer_phone_tel);
  const waHref = whatsappHref(info?.customer_phone || info?.customer_phone_tel);
  const hasWhatsApp = Boolean(waHref && waHref !== "#" && onStartWhatsAppCall);
  const [remaining, setRemaining] = useState(call.remaining_seconds ?? 0);
  const isAfter = phase === "after";

  useEffect(() => {
    if (confirmed || done) return;
    setRemaining(call.remaining_seconds ?? 0);
    if (!call.started_at) return undefined;
    const tick = setInterval(() => {
      setRemaining((r) => Math.max(0, r - 1));
    }, 1000);
    return () => clearInterval(tick);
  }, [call.started_at, call.remaining_seconds, call.is_confirmed, confirmed, done]);

  const canTapDone = confirmed || (call.started_at && remaining <= 0);

  if (done || confirmed) {
    if (variant === "driver") {
      return (
        <div className="driver-call-card driver-call-card--done driver-call-card--compact">
          <CheckCircle2 size={20} aria-hidden />
          <span>{isAfter ? "Final call done" : "Call done"}</span>
        </div>
      );
    }
    return (
      <div className="rounded-3xl border border-green-300 bg-green-50 p-4">
        <div className="flex items-center gap-2 text-green-800">
          <CheckCircle2 className="h-5 w-5 shrink-0" />
          <p className="text-sm font-extrabold">{isAfter ? "Final call done" : "Customer call done"}</p>
        </div>
      </div>
    );
  }

  const timerBlock =
    call.started_at && !confirmed ? (
      <div className={variant === "driver" ? "driver-call-timer" : "mt-2 flex items-center justify-center gap-2 rounded-xl bg-white/80 px-3 py-2 text-sm font-bold text-[#9D174D]"}>
        <Clock className="h-4 w-4 shrink-0" />
        {remaining > 0 ? (
          <span>{formatCountdown(remaining)}</span>
        ) : (
          <span>Tap Call done</span>
        )}
      </div>
    ) : null;

  if (variant === "driver") {
    return (
      <div className="driver-call-card driver-call-card--compact">
        <div className="driver-call-actions">
          {callHref ? (
            <button
              type="button"
              disabled={busy || starting}
              onClick={onStartCall}
              className="driver-call-round-btn"
              aria-label="Phone call customer"
            >
              <Phone size={22} aria-hidden />
            </button>
          ) : null}
          {hasWhatsApp ? (
            <button
              type="button"
              disabled={busy || starting}
              onClick={onStartWhatsAppCall}
              className="driver-call-wa-round-btn"
              aria-label="WhatsApp call customer"
            >
              <MessageCircle size={22} aria-hidden />
            </button>
          ) : null}
          {!callHref && !hasWhatsApp ? (
            <span className="driver-call-missing">No phone</span>
          ) : null}
        </div>
        {timerBlock}
        <button
          type="button"
          disabled={busy || completing || !canTapDone}
          onClick={onCompleteCall}
          className="driver-call-done-round"
        >
          {completing ? "Saving…" : "Call done"}
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-3xl border-2 border-pink-300 bg-pink-50 p-4">
      {timerBlock}
      <div className="mt-3 flex flex-col gap-2">
        {callHref || hasWhatsApp ? (
          <div className="flex gap-2">
            {callHref ? (
              <Button
                type="button"
                disabled={busy || starting}
                onClick={onStartCall}
                className="h-12 flex-1 rounded-full bg-[#EC4899] text-sm font-extrabold text-white hover:bg-[#DB2777]"
              >
                <Phone className="mr-1.5 h-5 w-5 shrink-0" />
                {starting ? "Starting…" : "Phone call"}
              </Button>
            ) : null}
            {hasWhatsApp ? (
              <Button
                type="button"
                disabled={busy || starting}
                onClick={onStartWhatsAppCall}
                className="h-12 flex-1 rounded-full bg-[#25D366] text-sm font-extrabold text-white hover:bg-[#20BD5A]"
              >
                <MessageCircle className="mr-1.5 h-5 w-5 shrink-0" />
                {starting ? "Starting…" : "WhatsApp call"}
              </Button>
            ) : null}
          </div>
        ) : (
          <p className="text-xs text-red-700">No customer phone on this order — ask manager.</p>
        )}
        <Button
          type="button"
          disabled={busy || completing || !canTapDone}
          onClick={onCompleteCall}
          className="h-12 rounded-full bg-[#DB2777] text-base font-extrabold text-white hover:bg-[#BE185D]"
        >
          {completing ? "Saving…" : "Customer call done"}
        </Button>
      </div>
    </div>
  );
}
